import { getBaseRole } from '@/helpers/customRoles';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Empty } from 'antd'
import type { LeadState } from '@/api/types/crm/crm.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useMedia } from 'react-use'
import useFullLeadModalAccess from '../hooks/useFullLeadModalAccess'
import { useLeadRefetch } from '../LeadRefetchContext'
import { resolveLeadWorkflowState } from '../leadFunnelId.utils'
import type {
  LeadProfileModalContentProps,
  LeadProfileModalContentRef,
} from '../LeadProfileModal.types'
import classes from '../index.module.css'
import LeadProfileModalBody from './LeadProfileModalBody'
import { buildFormDataFromLead } from './leadProfileContent.utils'
import { useLeadProfileFields } from './useLeadProfileFields'
import { useLeadProfileManager } from './useLeadProfileManager'
import { useLeadProfileSave } from './useLeadProfileSave'
import { useLeadProfileTrafficChannel } from './useLeadProfileTrafficChannel'
import { useLeadProfileDealUtm } from './useLeadProfileDealUtm'
import { isLeadPhoneChangedInForm } from '../../../utils/leadFields'
import UpdateIntegrationsOnPhoneChangeModal from '../components/UpdateIntegrationsOnPhoneChangeModal/UpdateIntegrationsOnPhoneChangeModal'
import DiscardLeadDraftChangesModal from '../components/DiscardLeadDraftChangesModal/DiscardLeadDraftChangesModal'
import {
  getCrmLeadParents,
  resolveActiveIntegrationTab,
  type LeadIntegrationContextTab,
  type ParentIntegrationDrafts,
} from '../blocks/MainInfoBlock/LeadIntegrationFieldsBlock/leadIntegrationContext.utils'

const LeadProfileModalContent = forwardRef<
  LeadProfileModalContentRef,
  LeadProfileModalContentProps
>(
  (
    {
      lead,
      setOpenModal,
      open,
      onClose,
      onClickByChildren,
      onClickByParent,
      onKanbanCardSync,
      onOpenVkChatWithLead,
      onOpenMaxChatWithLead,
      onOpenTelegramChatWithLead,
      onOpenWazzupChatWithLead,
    },
    ref,
  ) => {
    const addAlert = useAddAlert()
    const { currentRole, ...access } = useFullLeadModalAccess()
    const isNarrowLeadHeader = useMedia('(max-width: 767px)', false)
    const {
      handleRefetchLeadInModal,
      handleRefetchLeadsOnClose,
      isRefetchingLeadCard,
      selectedFunnelId,
      selectedFunnelIdRef,
      multipleDealsEnabled,
      selectedLeadFunnelId,
      committedManagerId,
      committedNextContactAt,
      committedCurrentStatusId,
      committedComment,
      initialFunnelIdForRefetch,
      resolveLeadFunnelId,
      getCardInFunnelSnapshot,
      cardFunnelSnapshotEpoch,
      leadCardSnapshot,
      patchLead,
    } = useLeadRefetch()

    const leadStateForWorkflow = useMemo(
      () =>
        resolveLeadWorkflowState({
          selectedFunnelId,
          getCardInFunnelSnapshot,
          isRefetchingLeadCard,
          globalType: lead?.type as LeadState | undefined,
        }),
      [
        cardFunnelSnapshotEpoch,
        getCardInFunnelSnapshot,
        isRefetchingLeadCard,
        lead?.type,
        selectedFunnelId,
      ],
    )

    const funnelNextContactDraftRef = useRef('')
    const dealCommentDraftRef = useRef('')
    const statusListRefreshRef = useRef<(() => void) | null>(null)
    const latestFunnelStatusIdRef = useRef<number | null>(null)
    const syncFunnelStatusDraft = useCallback((id: number | null) => {
      latestFunnelStatusIdRef.current = id
    }, [])

    const syncKanbanCardAfterMutation = useCallback(() => {
      const id = resolveLeadFunnelId()
      if (id != null && Number.isFinite(id) && id > 0) {
        onKanbanCardSync?.(id)
      }
    }, [resolveLeadFunnelId, onKanbanCardSync])

    const [selectTabKey, setSelectTabKey] = useState<string>('1')
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0)

    const {
      leadFields,
      isLoadingLeadFields,
      formData,
      setFormData,
      identityFieldErrors,
      setIdentityFieldErrors,
      handleChangeFieldData,
      handleClearIdentityFieldError,
    } = useLeadProfileFields({
      open,
      lead,
      selectedFunnelId,
    })

    const {
      selectedManagerId,
      selectedManagerIdRef,
      isManagerExplicitlyClearedRef,
      handleSelectManager,
      handleClearManager,
      resetManagerToCommitted,
    } = useLeadProfileManager({
      lead,
      selectedFunnelId,
      committedManagerId,
    })

    const {
      trafficChannelDraftId,
      setTrafficChannelDraftId,
      trafficChannelCurrent,
      resetTrafficChannelDraft,
    } = useLeadProfileTrafficChannel({
      lead,
      multipleDealsEnabled,
      selectedFunnelId,
      selectedLeadFunnelId,
      cardFunnelSnapshotEpoch,
      getCardInFunnelSnapshot,
    })

    const {
      dealUtmDraft,
      committedDealUtm,
      handleChangeDealUtmField,
      resetDealUtmDraft,
    } = useLeadProfileDealUtm({
      multipleDealsEnabled,
      selectedFunnelId,
      selectedLeadFunnelId,
      cardFunnelSnapshotEpoch,
      getCardInFunnelSnapshot,
    })

    const [discardDraftModalOpen, setDiscardDraftModalOpen] = useState(false)
    const [draftResetKey, setDraftResetKey] = useState(0)
    const [integrationContextTab, setIntegrationContextTab] =
      useState<LeadIntegrationContextTab>({ type: 'lead' })
    const [parentIntegrationDrafts, setParentIntegrationDrafts] =
      useState<ParentIntegrationDrafts>({})

    const crmParents = useMemo(() => getCrmLeadParents(lead), [lead])

    useEffect(() => {
      setIntegrationContextTab((prev) =>
        resolveActiveIntegrationTab(prev, crmParents),
      )
      setParentIntegrationDrafts((prev) => {
        const next: ParentIntegrationDrafts = {}
        for (const parent of crmParents) {
          const draft = prev[parent.id]
          if (draft && Object.keys(draft).length > 0) {
            next[parent.id] = draft
          }
        }
        return next
      })
    }, [crmParents])

    const clearParentIntegrationDrafts = useCallback(() => {
      setParentIntegrationDrafts({})
    }, [])

    const handleParentIntegrationFieldChange = useCallback(
      (parentId: number, fieldId: number, value: string) => {
        setParentIntegrationDrafts((prev) => ({
          ...prev,
          [parentId]: {
            ...(prev[parentId] ?? {}),
            [String(fieldId)]: value,
          },
        }))
      },
      [],
    )

    const discardDraftChanges = useCallback(() => {
      setFormData(buildFormDataFromLead(lead))
      setIdentityFieldErrors({})
      setParentIntegrationDrafts({})
      resetManagerToCommitted()
      resetTrafficChannelDraft()
      resetDealUtmDraft()
      latestFunnelStatusIdRef.current = committedCurrentStatusId
      funnelNextContactDraftRef.current = committedNextContactAt ?? ''
      dealCommentDraftRef.current = committedComment ?? ''
      setDraftResetKey((prev) => prev + 1)
    }, [
      committedComment,
      committedCurrentStatusId,
      committedNextContactAt,
      lead,
      resetDealUtmDraft,
      resetManagerToCommitted,
      resetTrafficChannelDraft,
      setFormData,
      setIdentityFieldErrors,
    ])

    const handleDiscardDraftOnly = useCallback(() => {
      discardDraftChanges()
      setDiscardDraftModalOpen(false)
      addAlert({
        title: 'Готово',
        text: 'Несохранённые изменения сброшены',
        type: 'success',
      })
    }, [addAlert, discardDraftChanges])

    const handleDiscardDraftAndClose = useCallback(() => {
      discardDraftChanges()
      setDiscardDraftModalOpen(false)
      onClose()
    }, [discardDraftChanges, onClose])

    const handleRequestCancel = useCallback(() => {
      if (access.canEditFields) {
        setDiscardDraftModalOpen(true)
        return
      }
      onClose()
    }, [access.canEditFields, onClose])

    const handleMoveToInWorkClick = useCallback(() => {
      const hasRealFunnel = (lead?.funnels ?? []).some((funnel) => {
        const id = Number(funnel?.id)
        return Number.isFinite(id) && id > 0
      })

      if (!hasRealFunnel) {
        addAlert({
          title: 'Ошибка',
          text: 'Сначала добавьте воронку',
          type: 'error',
        })
        return
      }

      setOpenModal({ type: 'change-lead-state' })
    }, [addAlert, lead?.funnels, setOpenModal])

    useImperativeHandle(ref, () => ({
      handleChangeFieldData,
      refreshHistory: () => {
        setHistoryRefreshKey((prev) => prev + 1)
      },
    }))

    const handlerSaveData = useLeadProfileSave({
      lead,
      currentRole,
      canEditFields: access.canEditFields,
      canChangeLeadStatus: access.canChangeLeadStatus,
      leadFields,
      formData,
      setFormData,
      setIdentityFieldErrors,
      selectedFunnelId,
      selectedFunnelIdRef,
      selectedLeadFunnelId,
      multipleDealsEnabled,
      committedManagerId,
      committedNextContactAt,
      committedCurrentStatusId,
      committedComment,
      selectedManagerIdRef,
      isManagerExplicitlyClearedRef,
      trafficChannelDraftId,
      funnelNextContactDraftRef,
      dealCommentDraftRef,
      dealUtmDraft,
      committedDealUtm,
      latestFunnelStatusIdRef,
      statusListRefreshRef,
      resolveLeadFunnelId,
      getCardInFunnelSnapshot,
      handleRefetchLeadInModal,
      handleRefetchLeadsOnClose,
      syncKanbanCardAfterMutation,
      buildFormDataFromLead,
      setHistoryRefreshKey,
      parentIntegrationDrafts,
      clearParentIntegrationDrafts,
      patchLead,
    })

    const [phoneIntegrationsModalOpen, setPhoneIntegrationsModalOpen] =
      useState(false)
    const [isPhoneIntegrationsSaving, setIsPhoneIntegrationsSaving] =
      useState(false)

    const handleSaveClick = useCallback(() => {
      if (isLeadPhoneChangedInForm(lead, formData)) {
        setPhoneIntegrationsModalOpen(true)
        return
      }

      void handlerSaveData({ updateIntegrations: false })
    }, [formData, handlerSaveData, lead])

    const handlePhoneIntegrationsSave = useCallback(
      async (updateIntegrations: boolean) => {
        setIsPhoneIntegrationsSaving(true)
        try {
          const ok = await handlerSaveData({ updateIntegrations })
          if (ok) {
            setPhoneIntegrationsModalOpen(false)
          }
        } finally {
          setIsPhoneIntegrationsSaving(false)
        }
      },
      [handlerSaveData],
    )
    const canUseFunnels = ['head-of-sales', 'admin', 'sales-manager'].includes(
      getBaseRole(currentRole),
    )
    const canRemoveFunnelsInCard = [
      'head-of-sales',
      'admin',
      'sales-manager',
    ].includes(getBaseRole(currentRole))
    const canEditManagerInCard = ['head-of-sales', 'admin'].includes(
      getBaseRole(currentRole),
    )
    const canViewManagerInCard = [
      'head-of-sales',
      'admin',
      'sales-manager',
    ].includes(getBaseRole(currentRole))
    // Смена воронки/сделки и сохранение — с blur/overlay.
    // Первичная подгрузка полей без уже готового списка тоже.
    const isLeadModalLoading =
      isRefetchingLeadCard ||
      (isLoadingLeadFields && leadFields.length === 0)

    const currentDealsCount = useMemo(() => {
      const list = leadCardSnapshot?.current_funnels
      return Array.isArray(list) ? list.length : 0
    }, [leadCardSnapshot])
    const canRemoveDealInCard =
      canRemoveFunnelsInCard && currentDealsCount > 1

    const shoppingCurrentLeadFunnelId = resolveLeadFunnelId()

    if (!lead) {
      return (
        <p className={classes.messageNoLead}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={'Лид не найден'}
          />
        </p>
      )
    }

    return (
      <>
        <LeadProfileModalBody
          lead={lead}
          access={{
            ...access,
            canDelete:
              access.canDelete ||
              (getBaseRole(currentRole) === 'franchise-manager' &&
                ['in_work', 'processed', 'client'].includes(
                  leadStateForWorkflow ?? '',
                )),
          }}
          isNarrowLeadHeader={isNarrowLeadHeader}
          isLeadModalLoading={isLeadModalLoading}
          selectedFunnelId={selectedFunnelId}
          initialFunnelIdForRefetch={initialFunnelIdForRefetch}
          leadFields={leadFields}
          formData={formData}
          selectedManagerId={selectedManagerId}
          canEditManagerInCard={canEditManagerInCard}
          canViewManagerInCard={canViewManagerInCard}
          identityFieldErrors={identityFieldErrors}
          trafficChannelCurrent={trafficChannelCurrent}
          trafficChannelDraftId={trafficChannelDraftId}
          setTrafficChannelDraftId={setTrafficChannelDraftId}
          dealUtmDraft={dealUtmDraft}
          onChangeDealUtmField={handleChangeDealUtmField}
          selectTabKey={selectTabKey}
          setSelectTabKey={setSelectTabKey}
          historyRefreshKey={historyRefreshKey}
          setHistoryRefreshKey={setHistoryRefreshKey}
          shoppingCurrentLeadFunnelId={shoppingCurrentLeadFunnelId}
          funnelNextContactDraftRef={funnelNextContactDraftRef}
          dealCommentDraftRef={dealCommentDraftRef}
          statusListRefreshRef={statusListRefreshRef}
          leadStateForWorkflow={leadStateForWorkflow}
          canUseFunnels={canUseFunnels}
          canRemoveFunnelsInCard={canRemoveFunnelsInCard}
          multipleDealsEnabled={multipleDealsEnabled}
          canRemoveDealInCard={canRemoveDealInCard}
          setOpenModal={setOpenModal}
          onClose={onClose}
          onClickByChildren={onClickByChildren}
          onClickByParent={onClickByParent}
          onOpenVkChatWithLead={onOpenVkChatWithLead}
          onOpenMaxChatWithLead={onOpenMaxChatWithLead}
          onOpenTelegramChatWithLead={onOpenTelegramChatWithLead}
          onOpenWazzupChatWithLead={onOpenWazzupChatWithLead}
          handleChangeFieldData={handleChangeFieldData}
          handleClearIdentityFieldError={handleClearIdentityFieldError}
          handleSelectManager={handleSelectManager}
          handleClearManager={handleClearManager}
          syncFunnelStatusDraft={syncFunnelStatusDraft}
          syncKanbanCardAfterMutation={syncKanbanCardAfterMutation}
          handleRefetchLeadInModal={handleRefetchLeadInModal}
          handleRefetchLeadsOnClose={handleRefetchLeadsOnClose}
          handleMoveToInWorkClick={handleMoveToInWorkClick}
          handlerSaveData={handleSaveClick}
          draftResetKey={draftResetKey}
          onRequestCancel={handleRequestCancel}
          integrationContextTab={integrationContextTab}
          onIntegrationContextTabChange={setIntegrationContextTab}
          parentIntegrationDrafts={parentIntegrationDrafts}
          onParentIntegrationFieldChange={handleParentIntegrationFieldChange}
        />
        <DiscardLeadDraftChangesModal
          open={discardDraftModalOpen}
          onDismiss={() => setDiscardDraftModalOpen(false)}
          onDiscard={handleDiscardDraftOnly}
          onDiscardAndClose={handleDiscardDraftAndClose}
        />
        <UpdateIntegrationsOnPhoneChangeModal
          open={phoneIntegrationsModalOpen}
          loading={isPhoneIntegrationsSaving}
          onDismiss={() => {
            if (isPhoneIntegrationsSaving) return
            setPhoneIntegrationsModalOpen(false)
          }}
          onKeepIntegrations={() => {
            void handlePhoneIntegrationsSave(false)
          }}
          onUpdateIntegrations={() => {
            void handlePhoneIntegrationsSave(true)
          }}
        />
      </>
    )
  },
)

LeadProfileModalContent.displayName = 'LeadProfileModalContent'

export default LeadProfileModalContent
